import { Component, Input, OnInit, ViewChild, ElementRef, TemplateRef,HostBinding } from '@angular/core';
import { NbDialogRef, NbDialogService } from '@nebular/theme';
import { GeneratorService, GifStatus, BestMatchAndPrompt } from 'src/app/deebr/service/generator.service';
import { interval, of, concat, EMPTY } from 'rxjs';
import { exhaustMap, tap, takeWhile, timeout, catchError, delay } from 'rxjs/operators';
import { HttpErrorResponse } from '@angular/common/http';

function decorateMessage(msg?: string, code?: string) {
  return code ? `${msg} (code support : ${code})` : msg;
}

@Component({
  selector: 'app-image-to-gif-dialog',
  templateUrl: './image-to-gif-dialog.component.html',
  styleUrls: ['./image-to-gif-dialog.component.scss'],
  standalone: false
})
export class ImageToGifDialogComponent implements OnInit {
  @ViewChild('image') image!: ElementRef<HTMLImageElement>;
  frameStyles: any = {};
  @Input() imageUrl: string = '';
  @Input() width_block: number = 0;
  progress: number = 0;
  gifUrl?: string;
  gifUrls: string[] = [];
  sizes: string[] = [];
  progressStarted: boolean = false;
  errorMessage?: string;
  dialogError: NbDialogRef<any> | undefined = undefined;
  best_match!: [number, number];
  prompt!: string;
  loading: boolean = true;
  bestMatchReady: boolean = false;
  imageLoaded: boolean = false;
  animationMode: string = "veo";
  phase: 'VIDEO_GENERATING' | 'GIF_RENDERING' | 'SUCCEEDED' | 'FAILED' | null = null;
  activeLabel = 'Animation de l’image…';
  activeProgress = 0;             // unique barre qui s'affiche
  wasInGifPhase = false;          // pour détecter la transition et reset la barre

  // optionnel: garde en mémoire les deux barres séparées si tu veux les afficher un jour
  progressVideo = 0;
  progressGif = 0;

  @HostBinding('class.loaded') isLoaded = false;

  @ViewChild('dialogErrorAnimate') dialogErrorAnimate!: TemplateRef<any>;

  constructor(
    private generatorService: GeneratorService,
    protected dialogRef: NbDialogRef<ImageToGifDialogComponent>,
    private dialogService: NbDialogService,
  ) {}

  ngOnInit(): void {
    this.requestBestMatch();
    setTimeout(() => {
      this.isLoaded = true;
    });
  }

  /** Step 1: fire off the bestMatchAndPrompt call, but don’t try to size yet */
  private requestBestMatch() {
    this.loading = true;
    this.generatorService.bestMatchAndPrompt(this.imageUrl)
      .subscribe({
        next: (resp: BestMatchAndPrompt) => {
          this.best_match = resp.best_match;
          this.prompt = resp.prompt;
          this.bestMatchReady = true;
          this.tryCalculateFrameSize();
        },
        error: (err) => {
          console.error('Failed to get frame size', err);
          this.loading = false;
        },
      });
  }

  /** Step 2: called by the <img> (load) event */
  onImageLoad() {
    this.imageLoaded = true;
    this.tryCalculateFrameSize();
  }

  /** Step 3: only when both flags are true… */
  private tryCalculateFrameSize() {
    if (!this.bestMatchReady || !this.imageLoaded) return;

    const [w, h] = this.best_match;
    const frameRatio = w / h;

    const imgEl = this.image.nativeElement;
    const dispW = imgEl.clientWidth;
    const dispH = imgEl.clientHeight;
    const imgRatio = dispW / dispH;

    if (imgRatio < frameRatio) {
      const fitHeightPx = dispW / frameRatio;
      
      const pct = fitHeightPx / dispH * 100;
      this.frameStyles = {
        width:  '100%',
        height: `calc(${pct}%)`
      };
    } else {
      const fitWidthPx = dispH * frameRatio;
      const pct = fitWidthPx / dispW * 100;
      this.frameStyles = {
        height: '100%',
        width:  `calc(${pct}%)`
      };
    }

    this.loading = false;
  }

  onSelectGif(url: string) {
    this.dialogRef.close(url);
  }
  
  onValidate(): void {
    this.progressStarted = true;
    this.activeProgress = 0;
    this.phase = 'VIDEO_GENERATING';
    this.activeLabel = 'Animation de l’image…';
    this.wasInGifPhase = false;

    this.generatorService.imageToGif(this.imageUrl, this.best_match, this.prompt, this.animationMode, this.width_block)
      .pipe(
        // NEW: early failure (if create itself fails)
        catchError((err: HttpErrorResponse) => {
          this.phase = 'FAILED';
          this.errorMessage = err.status === 422
            ? "L'image ou le prompt ne respecte pas les guidelines de notre IA."
            : "Impossible de démarrer l’animation.";
          this.dialogError = this.dialogService.open(this.dialogErrorAnimate);
          // Close dialog so parent overlay disappears
          this.dialogRef.close();
          return EMPTY;
        })
      )
      .subscribe(response => {
        const taskId = response.task_id;
        if (taskId === 'error') {
          this.phase = 'FAILED';
          this.errorMessage = "Une erreur s'est produite lors de la génération du GIF.";
          this.dialogError = this.dialogService.open(this.dialogErrorAnimate);
          this.dialogRef.close();
          return;
        }

        interval(2000).pipe(
          exhaustMap(() => this.generatorService.imageToGifProgress(taskId)),
          tap((status) => {
            // Handle progress → unchanged parts elided for brevity
            const phase = status.phase || (status.status === 'SUCCEEDED'
              ? 'SUCCEEDED'
              : this.phase ?? 'VIDEO_GENERATING');

            if (phase === 'GIF_RENDERING' && this.phase !== 'GIF_RENDERING' && !this.wasInGifPhase) {
              this.wasInGifPhase = true;
              this.activeProgress = 0;
              this.activeLabel = 'Création du GIF…';
            }
            this.phase = phase;

            if (this.phase === 'VIDEO_GENERATING') {
              const p = status.progress ?? 0;
              this.progressVideo = Math.max(0, Math.min(100, p <= 1 ? p * 100 : p));
              this.activeProgress = this.progressVideo;
              this.activeLabel = 'Animation de l’image…';
            } else if (this.phase === 'GIF_RENDERING') {
              const gp = status.gif_progress;
              if (gp != null) {
                this.progressGif = Math.max(0, Math.min(100, gp));
                this.activeProgress = this.progressGif;
              }
              this.activeLabel = 'Création du GIF…';
            }

            // SUCCESS
            if (status.status === 'SUCCEEDED') {
              this.phase = 'SUCCEEDED';
              this.activeProgress = 100;
              this.activeLabel = 'Terminé !';
              this.gifUrl = status.gif_url || '';
              if (status.gif_urls?.length) {
                this.gifUrls = status.gif_urls;
                this.sizes = status.sizes ?? [];
              }
              if (!this.gifUrls?.length) {
                this.dialogRef.close(this.gifUrl);
              }
            }

            // FAILURE (includes 422 mapped by the service)
            if (status.status === 'FAILED') {
              this.phase = 'FAILED';
              // sans champ error = échec modération Runway/Kling ; rai_filtered = Veo ; le reste est technique
              this.errorMessage = !status.error || status.error === 'rai_filtered'
                ? "L'image ou le prompt ne respecte pas les guidelines de notre IA."
                : "Erreur technique lors de l’animation.";
              this.dialogError = this.dialogService.open(this.dialogErrorAnimate);
              this.dialogRef.close(); // signal parent to remove overlay
            }
          }),
          takeWhile(s => s.status !== 'SUCCEEDED' && s.status !== 'FAILED', true)
        ).subscribe();
      });
  }
  

  onCancel(): void {
    this.dialogRef.close();
  }

  closeDialogError() {
    this.dialogError?.close();
  }

}